import React, { FC, useState, useMemo, useCallback } from 'react';
import { DATABASES_CRITERIA_SET } from './contexts/databases/domain/criteria';
import { COMPUTE_CRITERIA_SET } from './contexts/compute/domain/criteria';
import { databaseOptions } from './contexts/databases/domain/options';
import { computeOptions } from './contexts/compute/domain/options';
import PromptRunner, {
  ComputedResult,
} from './shared/prompts/infrastructure/PromptRunner';
import { CriteriaSet } from './shared/criteria/domain/criteria';
import { Option } from './shared/options/domain/option';
import { Choice } from './shared/choices/domain/choices';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { choices } from './choices';
import TechnologyChoiceResults from './TechnologyChoiceResults';

type TechnologyChoiceRunnerProps = { choice: Choice };
const TechnologyChoiceRunner: FC<TechnologyChoiceRunnerProps> = ({
  choice,
}) => {
  const config: {
    [key in Choice]: {
      criteriaSet: CriteriaSet<string>;
      options: Record<string, Option<string>>;
    };
  } = {
    databases: {
      criteriaSet: DATABASES_CRITERIA_SET,
      options: databaseOptions,
    },
    compute: {
      criteriaSet: COMPUTE_CRITERIA_SET,
      options: computeOptions,
    },
  };
  const promptRunner = new PromptRunner(
    config[choice].criteriaSet,
    Object.values(config[choice].options),
  );
  const questions = promptRunner.mapQuestions();

  const [questionIndex, setQuestionIndex] = useState<number>(0);
  const currentQuestion = useMemo(
    () => questions[questionIndex],
    [questions, questionIndex],
  );
  const [responses, setResponses] = useState<
    Record<string, { criteriaKey: string; value: number }>
  >({});
  const [displayResults, setDisplayResults] = useState<boolean>(false);

  const [computedResults, setComputedResults] = useState<ComputedResult[]>([]);

  const questionValue = useMemo(
    () => responses[currentQuestion.name]?.value.toString() || null,
    [responses, currentQuestion],
  );

  const handleChange = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
      setResponses({
        ...responses,
        [currentQuestion.name]: {
          criteriaKey: currentQuestion.name,
          value: parseInt(value, 10),
        },
      });
    },
    [responses, currentQuestion],
  );

  const handleFinish = () => {
    const r: Record<string, number> = {};
    Object.entries(responses).forEach(([key, current]) => {
      r[key] = current.value;
    });
    const _computedResults = promptRunner.computeResults(r);
    setComputedResults(_computedResults);
    setDisplayResults(true);
  };

  return (
    <>
      {displayResults ? (
        <TechnologyChoiceResults
          results={computedResults}
          responses={responses}
          criteriaSet={config[choice].criteriaSet}
        />
      ) : (
        <>
          <Typography variant="subtitle1" align="center" gutterBottom>
            {choices[choice].text}
          </Typography>
          <hr />
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            spacing={4}
          >
            <Grid item md={8}>
              <FormControl>
                <FormLabel id={`${currentQuestion.name}-label`}>
                  <Box my={4}>
                    <Typography variant="h5" align="center" gutterBottom>
                      {currentQuestion.message}
                    </Typography>
                  </Box>
                </FormLabel>
                <RadioGroup
                  id={currentQuestion.name}
                  name={currentQuestion.name}
                  aria-labelledby={`${currentQuestion.name}-label`}
                  value={questionValue}
                  onChange={(event, value) => handleChange(event, value)}
                >
                  <Grid container alignItems="center">
                    {currentQuestion.choices.map(c => (
                      <Grid item key={c.title} md={6} zeroMinWidth>
                        <Box my={2} key={`${currentQuestion.name}-${c.title}`}>
                          <Typography variant="body1">
                            <FormControlLabel
                              value={c.value.toString()}
                              control={<Radio />}
                              label={c.title}
                            />
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          <hr />
          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="center"
            alignContent="center"
          >
            <Grid item xs={12}>
              <Grid container justifyContent="center">
                {questionIndex > 0 && (
                  <Grid item>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setQuestionIndex(_questionIndex => _questionIndex - 1);
                      }}
                    >
                      Back
                    </Button>
                  </Grid>
                )}
                {questionIndex < questions.length - 1 && (
                  <Grid item>
                    <Button
                      disabled={!questionValue}
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setQuestionIndex(_questionIndex => _questionIndex + 1);
                      }}
                    >
                      Next
                    </Button>
                  </Grid>
                )}
                {questionIndex === questions.length - 1 && (
                  <Grid item>
                    <Button
                      disabled={!questionValue}
                      variant="outlined"
                      color="primary"
                      onClick={handleFinish}
                    >
                      Finish
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default TechnologyChoiceRunner;
